import { useCallback, useEffect, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import {
  fetchSpectatorFeed,
  submitReaction,
  submitComment,
  overrideVerdict,
} from '../lib/spectatorApi.js';
import Leaderboard from '../components/Leaderboard.jsx';
import EndTripPanel from '../components/EndTripPanel.jsx';
import { cleanWilmanText } from '../lib/wilmanText.js';
import { formatTime, formatDateShort } from '../lib/format.js';

const REACTIONS = [
  { id: 'heart', glyph: '♥', label: 'Heart' },
  { id: 'eye-roll', glyph: '👁', label: 'Eye roll' },
  { id: 'laugh', glyph: 'ha', label: 'Laugh' },
  { id: 'awe', glyph: '✦', label: 'Awe' },
  { id: 'suspicious', glyph: '?', label: 'Suspicious' },
];

export default function Spectator() {
  const { trip, tripId } = useTrip();
  const session = useSession();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOverrideId, setOpenOverrideId] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchSpectatorFeed({ tripId });
      setFeed(data);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Gate: only Anar (or any spectator-judge) sees this dashboard.
  // During Phase 5 we tolerate the SessionProvider being still-loading
  // so the page doesn't flicker into a "no access" state.
  if (session.loading) {
    return (
      <div className="mx-auto w-full max-w-screen-sm p-6 text-center font-mono text-xs uppercase tracking-wider text-fg-tertiary">
        …verifying credentials
      </div>
    );
  }
  if (!session.ok || session.role !== 'spectator-judge') {
    return (
      <div className="mx-auto w-full max-w-screen-sm space-y-3 p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          spectator dashboard
        </p>
        <h1 className="font-serif text-2xl">Senior Producer · access required</h1>
        <p className="text-fg-secondary">
          This view is for Anar. Open the briefing email link on this device, enter the access
          phrase, and you&apos;ll land here automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-5 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          senior producer · live
        </p>
        <h1 className="mt-1 font-serif text-2xl">Spectator</h1>
        <p className="text-sm text-fg-secondary">
          Read everywhere. React, comment, override Wilman within 24h.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}

      {loading && !feed && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">…tuning in</p>
      )}

      {feed?.leaderboard && (
        <Leaderboard
          leaderboard={feed.leaderboard}
          players={trip?.players || feed.players || []}
          activePlayerId={null}
        />
      )}

      <EndTripPanel />

      {feed?.today?.transmissions?.length > 0 && (
        <Section title={`today's transmissions · ${feed.today.day_key}`}>
          <ul className="space-y-2">
            {feed.today.transmissions.map((t) => (
              <TransmissionItem key={t.id} transmission={t} timezone={trip?.dates?.timezone} />
            ))}
          </ul>
        </Section>
      )}

      {feed?.trip_roll?.length > 0 && (
        <Section title={`trip roll · ${feed.trip_roll.length}`}>
          <ul className="grid grid-cols-3 gap-2">
            {feed.trip_roll.map((r) => {
              const player = (feed.players || []).find((p) => p.id === r.player_id);
              return (
                <li key={r.id} className="card overflow-hidden">
                  <div className="aspect-square bg-black">
                    {r.photo_url ? (
                      r.resource_type === 'video' ? (
                        <video
                          src={r.photo_url}
                          controls
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={r.photo_url}
                          alt={r.caption || ''}
                          className="h-full w-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      )
                    ) : null}
                  </div>
                  <div className="space-y-0.5 px-2 py-1">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {player?.name || r.player_id}
                    </p>
                    {r.caption && (
                      <p className="line-clamp-2 text-[11px] text-fg-secondary">{r.caption}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <Section title={`recent submissions · ${feed?.submissions?.length || 0}`}>
        {feed?.submissions?.length === 0 && (
          <div className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
            No submissions yet. Wilman has been quiet.
          </div>
        )}
        <ul className="space-y-3">
          {feed?.submissions?.map((s) => {
            const interaction = feed.interactions.find((i) => i.id === s.id) || { reactions: [], comments: [] };
            const player = (feed.players || []).find((p) => p.id === s.player_id);
            return (
              <SubmissionCard
                key={s.id}
                submission={s}
                player={player}
                interaction={interaction}
                onReact={async (reaction) => {
                  await submitReaction({ tripId, submissionId: s.id, reaction });
                  await refresh();
                }}
                onComment={async (body) => {
                  await submitComment({ tripId, submissionId: s.id, body });
                  await refresh();
                }}
                openOverride={openOverrideId === s.id}
                onToggleOverride={() => setOpenOverrideId(openOverrideId === s.id ? null : s.id)}
                onOverride={async (newPoints, reason) => {
                  await overrideVerdict({ tripId, submissionId: s.id, newPoints, reason });
                  setOpenOverrideId(null);
                  await refresh();
                }}
              />
            );
          })}
        </ul>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary">{title}</h2>
      {children}
    </section>
  );
}

function TransmissionItem({ transmission, timezone }) {
  const isRecap = transmission.kind === 'recap' || transmission.fired_reason === 'recap';
  const cardClasses = isRecap
    ? 'card border-copper/40 bg-copper/5 px-4 py-3'
    : 'card px-4 py-3';
  return (
    <li className={cardClasses}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {isRecap ? <span className="text-copper-dark">producer · </span> : null}
        {transmission.challenge_title} · {formatTime(transmission.fired_at, { timezone, withZone: false })}
      </p>
      <p className={`mt-1 ${isRecap ? '' : 'line-clamp-2'} font-serif text-sm italic text-fg-secondary`}>
        {cleanWilmanText(transmission.challenge_text)}
      </p>
      {transmission.audio_url && (
        <audio
          controls
          preload="none"
          src={transmission.audio_url}
          className="mt-2 w-full"
        >
          Your browser does not support the audio element.
        </audio>
      )}
    </li>
  );
}

function SubmissionCard({
  submission,
  player,
  interaction,
  onReact,
  onComment,
  openOverride,
  onToggleOverride,
  onOverride,
}) {
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [overrideValue, setOverrideValue] = useState(submission.points);
  const [overrideReason, setOverrideReason] = useState('');
  const [submittingOverride, setSubmittingOverride] = useState(false);
  const [error, setError] = useState(null);

  const ageMs = Date.now() - new Date(submission.submitted_at).getTime();
  const overrideOpen = ageMs < 24 * 3600 * 1000;
  const myReaction = interaction.reactions.find((r) => r.spectator_id);

  return (
    <li className="card overflow-hidden">
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        <span>
          {player?.name || submission.player_id} · {submission.challenge_id}
        </span>
        <span>{formatDateShort(submission.submitted_at, { timezone: 'America/Juneau' })}</span>
      </header>

      {submission.photo_url && (
        <img
          src={submission.photo_url}
          alt=""
          className="h-44 w-full object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      )}
      {Array.isArray(submission.photo_urls) && (
        <div className="grid grid-cols-3 gap-px bg-border-tertiary">
          {submission.photo_urls.slice(0, 3).map((u, i) => (
            <img key={i} src={u} alt="" className="aspect-square w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
          ))}
        </div>
      )}

      <div className="space-y-3 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span
            className={[
              'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
              submission.verified
                ? 'border-success/40 bg-success/10 text-success'
                : 'border-danger/40 bg-danger/10 text-danger',
            ].join(' ')}
          >
            {submission.verified ? 'verified' : 'rejected'}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {submission.points} pts{submission.original_points != null && submission.original_points !== submission.points
              ? ` · was ${submission.original_points}`
              : ''}
          </span>
        </div>

        {submission.wilman_commentary && (
          <p className="font-serif italic text-sm text-fg-primary">
            &ldquo;{submission.wilman_commentary}&rdquo;
          </p>
        )}

        {(submission.camera_caption ||
          (submission.gps_mismatch_meters != null && submission.gps_mismatch_meters > 500)) && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {submission.camera_caption || ''}
            {submission.gps_mismatch_meters != null && submission.gps_mismatch_meters > 500 ? (
              <span className="ml-2 text-copper-dark">
                · GPS Δ {Math.round(submission.gps_mismatch_meters / 100) / 10} km vs claimed
              </span>
            ) : null}
          </p>
        )}

        <ReactionRow
          mine={myReaction?.reaction || null}
          onReact={async (r) => {
            setError(null);
            try {
              await onReact(r === myReaction?.reaction ? null : r);
            } catch (err) {
              setError(err.message || String(err));
            }
          }}
        />

        {interaction.comments?.length > 0 && (
          <ul className="space-y-1.5 rounded-md bg-bg-secondary p-2">
            {interaction.comments.map((c) => (
              <li key={c.id} className="text-sm">
                <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  {c.spectator_id}
                </span>
                <span className="ml-2 text-fg-primary">{c.body}</span>
              </li>
            ))}
          </ul>
        )}

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            setSubmittingComment(true);
            setError(null);
            try {
              await onComment(comment.trim());
              setComment('');
            } catch (err) {
              setError(err.message || String(err));
            } finally {
              setSubmittingComment(false);
            }
          }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="comment…"
            className="flex-1 rounded-md border border-border-tertiary bg-bg-primary px-3 py-1.5 text-sm placeholder:text-fg-tertiary focus:border-action focus:outline-none"
          />
          <button
            type="submit"
            disabled={submittingComment || !comment.trim()}
            className="rounded-md border border-action bg-action px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-action-fg transition disabled:opacity-60"
          >
            {submittingComment ? '…' : 'post'}
          </button>
        </form>

        <div>
          <button
            type="button"
            onClick={onToggleOverride}
            disabled={!overrideOpen}
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline disabled:opacity-50"
          >
            {!overrideOpen
              ? 'override window closed (24h)'
              : openOverride
                ? 'cancel override'
                : 'override verdict'}
          </button>

          {openOverride && overrideOpen && (
            <div className="mt-2 space-y-2 rounded-md border border-copper/40 bg-copper/5 p-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
                override panel · senior producer
              </p>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  new point value
                </span>
                <input
                  type="number"
                  value={overrideValue}
                  onChange={(e) => setOverrideValue(parseInt(e.target.value, 10) || 0)}
                  className="mt-1 w-full rounded-md border border-border-tertiary bg-bg-primary px-3 py-1.5 text-sm focus:border-action focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  reason (optional)
                </span>
                <input
                  type="text"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="why?"
                  className="mt-1 w-full rounded-md border border-border-tertiary bg-bg-primary px-3 py-1.5 text-sm focus:border-action focus:outline-none"
                />
              </label>
              <button
                type="button"
                onClick={async () => {
                  setSubmittingOverride(true);
                  setError(null);
                  try {
                    await onOverride(overrideValue, overrideReason || undefined);
                  } catch (err) {
                    setError(err.message || String(err));
                  } finally {
                    setSubmittingOverride(false);
                  }
                }}
                disabled={submittingOverride}
                className="w-full rounded-md border border-copper bg-copper px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-action-fg transition disabled:opacity-60"
              >
                {submittingOverride ? 'overriding…' : 'commit override'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <p className="rounded-md border border-danger/40 bg-danger/10 px-2 py-1 font-mono text-[10px] text-danger">
            {error}
          </p>
        )}
      </div>
    </li>
  );
}

function ReactionRow({ mine, onReact }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {REACTIONS.map((r) => {
        const isMine = mine === r.id;
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => onReact(r.id)}
            aria-pressed={isMine}
            className={[
              'flex items-center gap-1 rounded-full border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition',
              isMine
                ? 'border-copper bg-copper/15 text-copper-dark'
                : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
            ].join(' ')}
          >
            <span aria-hidden>{r.glyph}</span>
            {r.label}
          </button>
        );
      })}
    </div>
  );
}
